/* eslint-disable */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import jsQR from 'jsqr';

import Button from 'components/atoms/Button';
import Icon from 'components/atoms/Icon';
import Text from 'components/atoms/Text';
import NotifyModal from 'components/templates/NotifyModal';
import useWindowSize from 'hooks/useWindowSize';
import { useAppSelector } from 'store/hooks';

interface CameraCaptureProps {
  name?: string;
  isSingle?: boolean;
  loading?: boolean;
  isError?: boolean;
  /** When true, renders only the video element inline inside its parent container (no full-screen overlay) */
  frameMode?: boolean;
  /** Called with the decoded QR string when a QR code is found (frameMode only) */
  handleQRCode?: (data: string) => void;
  handleFinish?: (photos: File[]) => void;
  handleCheckFaceId?: (photos: File[]) => void;
  handleBack?: (photos?: File[]) => void;
  handleResetError?: () => void;
  notifySuccess?: {
    title: string;
    desc: string;
  }
  isConfirm?: boolean;
}

const CAPTURE_OPTIONS = {
  audio: false,
  video: {
    facingMode: 'user',
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { max: 30 },
    aspectRatio: 16 / 9,
  },
};

// Camera options optimised for QR scanning (rear camera, square-ish).
// `advanced` constraints disable Portrait Mode / background blur on devices that support it
// (Safari iOS 17+, Chrome on Android). Non-supported devices simply ignore unknown keys.
const QR_CAPTURE_OPTIONS = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1280 },
    height: { ideal: 1280 },
    advanced: [
      {
        // Disable background blur / Portrait Mode effect
        backgroundBlur: false,
        // Disable depth-based segmentation mask (some browsers use this key instead)
        backgroundSegmentationMask: false,
      } as MediaTrackConstraintSet,
    ],
  },
};

export interface CameraCaptureRef {
  handleReCapture: () => void;
}

const CameraCapture = forwardRef<CameraCaptureRef, CameraCaptureProps>(
  ({
    name,
    isSingle,
    loading,
    isError,
    frameMode,
    handleQRCode,
    handleFinish,
    handleBack,
    handleResetError,
    notifySuccess,
    isConfirm,
    handleCheckFaceId,
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState(false);
    const [preview, setPreview] = useState('');
    const [photos, setPhotos] = useState<File[]>([]);
    const [errRequest, setErrRequest] = useState(false);
    const [isSupport, setIsSupport] = useState(false);
    /**
     * Tăng giá trị này sẽ force re-mount toàn bộ stream + effect camera.
     * Được dùng khi mạng khôi phục để restart camera tự động.
     */
    const [cameraKey, setCameraKey] = useState(0);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    // Tracks whether a QR scan result has already been delivered (avoids duplicate callbacks)
    const scanActiveRef = useRef(true);

    const { height: windowHeight } = useWindowSize();
    const isOnline = useAppSelector((state) => state.system.isOnline);
    const prevIsOnlineRef = useRef(isOnline);

    const stopSelfie = async () => {
      await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
        .then((sm) => {
          if (!videoRef.current) return;
          videoRef.current.srcObject = sm;
          sm.getTracks().forEach((track) => {
            track.stop();
          });
        });
    };

    const handleCapture = useCallback(async () => {
      if (videoRef.current && canvasRef.current) {
        // const width = videoRef.current.videoWidth;
        // const height = videoRef.current.videoHeight;
        //* Workaround
        const isPortrait = window.innerWidth < window.innerHeight;
        const rotatedHeight = videoRef.current.videoHeight > videoRef.current.videoWidth
          ? videoRef.current.videoHeight
          : videoRef.current.videoWidth;
        const rotatedWidth = videoRef.current.videoWidth > videoRef.current.videoHeight
          ? videoRef.current.videoHeight
          : videoRef.current.videoWidth;
        const width = isPortrait
          ? rotatedWidth
          : videoRef.current.videoWidth;
        const height = isPortrait
          ? rotatedHeight
          : videoRef.current.videoHeight;

        const ctx = canvasRef.current.getContext('2d');
        const maxWidth = width > height ? 1280 : 720;
        const maxHeight = width > height ? 720 : 1280;

        let ratio = Math.min(maxWidth / width, maxHeight / height);
        if (ratio > 1) {
          ratio = 1;
        }

        const newWidth = width * ratio;
        const newHeight = height * ratio;
        canvasRef.current.width = newWidth;
        canvasRef.current.height = newHeight;
        if (ctx) {
          // if (isMobile) {
          ctx.translate(newWidth, 0);
          ctx.scale(-1, 1);
          // }
          ctx.drawImage(videoRef.current, 0, 0, newWidth, newHeight);
          //* Watermark
          // ctx.fillStyle = '#fff';
          // ctx.shadowColor = '#000';
          // ctx.shadowBlur = 4;
          // ctx.textBaseline = 'middle';
          // ctx.font = '32px sans-serif';
          // ctx.textAlign = 'right';

          // const x = newWidth - 16;
          // let y = 32;
          // const lineHeight = ctx.measureText('M').width * 1.2;
          // const lines = `${dayjs(new Date()).format('DD MMM YYYY [at] hh:mm:ss A')}`.split('\n');
          // lines.forEach((ele) => {
          //   ctx.fillText(ele, x, y);
          //   y += lineHeight;
          // });

          const imageDataURL = canvasRef.current.toDataURL('image/jpeg');
          setPreview(imageDataURL);
        }
        canvasRef.current.toBlob(async (b) => {
          if (b) {
            const file = new File([b], `${name}-${photos.length}.jpeg`, { type: 'image/jpeg' });
            setPhotos(isSingle ? [file] : [...photos, file]);
            if (handleCheckFaceId) {
              handleCheckFaceId([file]);
            }
          }
        }, 'image/jpeg');
      }
    }, [
      isSingle,
      name,
      photos,
      // isMobile,
      handleCheckFaceId,
    ]);

    const handleReset = useCallback(() => {
      setPhotos([]);
      setIsOpen(false);
      setPreview('');
      setError(false);
      if (handleResetError) handleResetError();
    }, [handleResetError]);

    const handleSubmit = useCallback(async () => {
      if (handleFinish) handleFinish(photos);
    }, [handleFinish, photos]);

    const onBack = useCallback(async () => {
      if (handleBack) {
        handleBack(photos);
      }
      await stopSelfie();
    }, [handleBack, photos]);

    const closeReview = useCallback(() => {
      if (isSingle) {
        setPhotos([]);
      }
      setPreview('');
    }, [isSingle]);

    useImperativeHandle(ref, () => ({
      handleReCapture: () => handleReset(),
    }));

    useEffect(() => {
      setError(!!isError);
      if (isError) {
        setIsOpen(false);
      }
    }, [isError]);

    /** Dừng stream + tăng cameraKey khi offline → re-mount camera khi online lại */
    useEffect(() => {
      const wasOnline = prevIsOnlineRef.current;
      prevIsOnlineRef.current = isOnline;

      if (!isOnline && wasOnline) {
        // Offline → dừng stream ngay lập tức
        if (videoRef.current?.srcObject) {
          const stream = videoRef.current.srcObject as MediaStream;
          stream.getTracks().forEach((track) => track.stop());
          videoRef.current.srcObject = null;
        }
      }

      if (isOnline && !wasOnline) {
        // Online lại → force re-mount bằng cách tăng cameraKey
        setCameraKey((k) => k + 1);
      }
    }, [isOnline]);

    useEffect(() => {
      if (!errRequest && isOnline) {
        const constraints = frameMode ? QR_CAPTURE_OPTIONS : CAPTURE_OPTIONS;
        const streamPromise = navigator.mediaDevices.getUserMedia(
          constraints,
        ).catch((err: any) => {
          if (err.name === 'NotAllowedError') {
            setErrRequest(true);
          } else {
            setIsSupport(true);
          }
          return null;
        });
        if (videoRef) {
          streamPromise.then((stream) => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            }
          });
        }
        return () => {
          streamPromise.then((stream) => stream?.getTracks().forEach((track) => {
            track.stop();
          }));
        };
      }
      return () => { };
    }, [errRequest, frameMode, cameraKey, isOnline]);

    // ── jsQR scan loop (frameMode only) ──────────────────────────────────────
    useEffect(() => {
      if (!frameMode || !handleQRCode) return;

      // Reset so a fresh mount can deliver a result
      scanActiveRef.current = true;

      const qrCanvas = document.createElement('canvas');
      // willReadFrequently hint tells the browser to optimise for frequent getImageData calls
      const qrCtx = qrCanvas.getContext('2d', { willReadFrequently: true });
      let rafId: number;
      // Track the timestamp of the last jsQR call to throttle CPU usage
      let lastScanTime = 0;
      // Only decode once every SCAN_INTERVAL_MS (≈6-7 fps) instead of every rAF frame (30+ fps)
      const SCAN_INTERVAL_MS = 150;
      // Max dimension for the offscreen canvas fed to jsQR.
      // Smaller canvas = far fewer pixels → jsQR runs ~10× faster with negligible accuracy loss.
      const MAX_SCAN_DIM = 400;

      const tick = (timestamp: number) => {
        const video = videoRef.current;
        if (
          !scanActiveRef.current
          || !video
          || video.readyState !== video.HAVE_ENOUGH_DATA
        ) {
          rafId = requestAnimationFrame(tick);
          return;
        }

        // Throttle: skip this frame if not enough time has passed
        if (timestamp - lastScanTime < SCAN_INTERVAL_MS) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        lastScanTime = timestamp;

        const { videoWidth: vw, videoHeight: vh } = video;
        // Downscale to at most MAX_SCAN_DIM on the longest side
        const scale = Math.min(MAX_SCAN_DIM / vw, MAX_SCAN_DIM / vh, 1);
        const w = Math.round(vw * scale);
        const h = Math.round(vh * scale);

        qrCanvas.width = w;
        qrCanvas.height = h;

        if (qrCtx) {
          // Rear camera is NOT mirrored – draw the frame directly
          qrCtx.drawImage(video, 0, 0, w, h);

          const imageData = qrCtx.getImageData(0, 0, w, h);
          const code = jsQR(imageData.data, w, h, {
            inversionAttempts: 'dontInvert',
          });
          console.log('code', code);
          if (code) {
            // Stop looping and deliver result once
            scanActiveRef.current = false;
            handleQRCode(code.data);
            return;
          }
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);

      return () => {
        cancelAnimationFrame(rafId);
      };
      // Re-run when frameMode or callback changes, or when scanActiveRef is reset from outside
    }, [frameMode, handleQRCode]);

    const renderUI = useMemo(() => {
      if (frameMode) {
        return (
          <>
            {/* Camera video fills the frame */}
            <video
              id={`video-${name}`}
              ref={videoRef}
              className="o-camera_video o-camera_video--frame"
              autoPlay
              playsInline
              controls={false}
              muted
            />
            <canvas
              id={`canvas-${name}`}
              ref={canvasRef}
              style={{ display: 'none' }}
            />
            {/* Frame corner accents */}
            <span className="o-camera_frame-corner o-camera_frame-corner--tl" />
            <span className="o-camera_frame-corner o-camera_frame-corner--tr" />
            <span className="o-camera_frame-corner o-camera_frame-corner--bl" />
            <span className="o-camera_frame-corner o-camera_frame-corner--br" />
            {/* Animated scan line */}
            <div className="o-camera_frame-scan-line" />
          </>
        );
      }

      return (
        <>
          <div className="o-camera_back" onClick={onBack}>
            <Icon iconName={preview ? 'arrowPrevWhite' : 'arrowCirclePrevWhite'} size="28" />
          </div>
          <div
            className="o-camera"
            style={{ maxHeight: `${windowHeight}px` }}
          >
            <video
              id={`video-${name}`}
              ref={videoRef}
              className="o-camera_video"
              autoPlay
              playsInline
              controls={false}
              muted
            />
            <canvas
              id={`canvas-${name}`}
              ref={canvasRef}
            />
            <div className="o-camera_capture">
              <div className="o-camera_capture-button">
                <Button iconName="cameraWhite" sizeIcon="24" modifiers={['lg', 'primary']} onClick={handleCapture} />
              </div>
            </div>
            {preview && (
              <div className="o-camera_preview" style={{ backgroundImage: `url(${preview})` }}>
                <div className="o-camera_capturePreview">
                  <div className="o-camera_capturePreview-button">
                    <Button disabled={loading} iconName="cameraGreen" sizeIcon="24" modifiers={['lg', 'outlineGreen']} onClick={closeReview}>
                      Chụp lại
                    </Button>
                  </div>
                  <div className="o-camera_capturePreview-button">
                    <Button disabled={loading} iconName="cameraGreen" sizeIcon="24" modifiers={['lg', 'primary']} onClick={handleSubmit}>
                      Xác nhận
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      );
    }, [frameMode, name, videoRef, canvasRef, onBack, preview, windowHeight, handleCapture, loading, closeReview, handleSubmit])

    return (
      <>
        {
          renderUI
        }
        <NotifyModal
          isOpen={isOpen}
          title={notifySuccess?.title}
          desc={notifySuccess?.desc}
          submitText="Đồng ý"
          backText="Chụp lại"
          handleBack={handleReset}
          handleSubmit={handleSubmit}
          loadingSubmit={loading}
        />
        <NotifyModal
          isError
          isOpen={error}
          title="Thất bại"
          desc="Đã có lỗi xảy ra. Vui lòng thử lại!"
          submitText="Chụp lại"
          handleSubmit={handleReset}
        />
        <NotifyModal
          isOpen={errRequest}
          desc="
            Chức năng này cần cho phép
            <br />
            truy cập camera
            <br />
            <br />
            Hướng dẫn bật truy cập camera
             <br />
            iOS: Vào cài đặt &gt; Safari &gt; Camera &gt; Chọn Cho phép
            <br />
            Android: Nhấn vào biểu tượng Khoá/Cài đặt ở thanh địa chỉ &gt; Cài đặt trang web &gt; Quyền truy cập camera &gt; Chọn Cho phép
            <br />"
          submitText="Ok"
          handleSubmit={() => setErrRequest(false)}
        />
        <NotifyModal
          isOpen={isSupport}
          desc="
            Trình duyệt hoặc thiết bị của bạn
            <br />
            không hỗ trợ camera để thực hiện tính năng này.
            <br />
            Vui lòng kiểm tra lại."
          submitText="Ok"
          handleSubmit={() => {
            setIsSupport(false);
            onBack();
          }}
        />
      </>
    );
  }
);

export default CameraCapture;
